import type { BetBundle } from '@/types';
import { amountsEqual, parseClickhouseTime, parseMysqlTime } from '@/lib/format';

/**
 * Cross-source reconciliation.
 *
 * The join key is the MySQL `transactions.id`. It is the one identifier that
 * appears in every source, under different names:
 *   MySQL     transactions.id        781675
 *   realtime  transaction_id         "781675"
 *   mv        transaction_id         "781675"
 *   sync      id                     781675
 * (`tr_uniq_id` is the fallback for realtime, which also carries it.)
 *
 * Everything is normalised to a string before comparing — the three ClickHouse
 * tables use String, Int64 and UUID types for overlapping concepts.
 *
 * The mv logs every ATTEMPT, including failures, so it can hold several rows per
 * transaction. We keep them all and surface the count.
 */

export const SOURCE_COLUMNS = [
  { key: 'mysql', label: 'Primary DB', glossary: 'source_transactions' },
  { key: 'realtime', label: 'Realtime', glossary: 'source_clickhouseRealtime' },
  { key: 'mv', label: 'Attempt log', glossary: 'source_clickhouseMv' },
  { key: 'sync', label: 'Settled', glossary: 'source_clickhouseSync' },
] as const;

export type SourceColumnKey = (typeof SOURCE_COLUMNS)[number]['key'];

export type CellState = 'present' | 'mismatch' | 'missing' | 'unknown';

export interface ReconCell {
  state: CellState;
  /** Field-level disagreements vs MySQL, e.g. "amount: 1.4 vs 1.5". */
  diffs: string[];
  /** mv can log the same transaction more than once (retries/failures). */
  occurrences: number;
  raw: Record<string, unknown> | null;
}

export interface ReconRow {
  transactionId: string;
  trUniqId: string | null;
  action: string;
  type: string;
  amount: string | null;
  currency: string | null;
  at: Date | null;
  cells: Record<SourceColumnKey, ReconCell>;
  /** True when any source disagrees with, or is missing vs, MySQL. */
  hasProblem: boolean;
}

const str = (v: unknown): string | null =>
  v === null || v === undefined || v === '' ? null : String(v);

function emptyCell(state: CellState): ReconCell {
  return { state, diffs: [], occurrences: 0, raw: null };
}

/**
 * A source that we never reached must read as `unknown`, NOT `missing`.
 * "We couldn't look" and "it isn't there" are completely different diagnoses and
 * conflating them would make this view lie.
 */
function baseState(status: string): CellState {
  return status === 'ok' || status === 'empty' ? 'missing' : 'unknown';
}

export function reconcile(bundle: BetBundle): ReconRow[] {
  const mysqlRows = bundle.transactions.data ?? [];

  const realtime = new Map<string, Record<string, unknown>>();
  for (const r of bundle.clickhouseRealtime.data ?? []) {
    const key = str(r.transaction_id);
    if (key) realtime.set(key, r as unknown as Record<string, unknown>);
  }

  // mv: group — it logs failed attempts too, so duplicates are expected.
  const mv = new Map<string, Record<string, unknown>[]>();
  for (const r of bundle.clickhouseMv.data ?? []) {
    const key = str(r.transaction_id);
    if (!key) continue;
    const list = mv.get(key) ?? [];
    list.push(r as unknown as Record<string, unknown>);
    mv.set(key, list);
  }

  const sync = new Map<string, Record<string, unknown>>();
  for (const r of bundle.clickhouseSync.data ?? []) {
    const key = str(r.id);
    if (key) sync.set(key, r as unknown as Record<string, unknown>);
  }

  const statuses = {
    realtime: bundle.clickhouseRealtime.status,
    mv: bundle.clickhouseMv.status,
    sync: bundle.clickhouseSync.status,
  };

  return mysqlRows.map((m) => {
    const id = String(m.id);
    // The row typed as GameAggrTransactionRow, viewed as a bag of fields — the
    // comparison is field-name driven and deliberately schema-agnostic.
    const mysqlRow = m as unknown as Record<string, unknown>;

    const cells: Record<SourceColumnKey, ReconCell> = {
      mysql: {
        state: 'present',
        diffs: [],
        occurrences: 1,
        raw: mysqlRow,
      },
      realtime: emptyCell(baseState(statuses.realtime)),
      mv: emptyCell(baseState(statuses.mv)),
      sync: emptyCell(baseState(statuses.sync)),
    };

    // --- realtime
    const rt = realtime.get(id) ?? findByTrUniq(bundle, m.tr_uniq_id);
    if (rt) {
      cells.realtime = compare(mysqlRow, rt, [
        'action',
        'type',
        'amount',
        'currency',
        'player_id',
      ]);
    }

    // --- mv (may be several rows)
    const mvRows = mv.get(id);
    if (mvRows?.length) {
      const cell = compare(mysqlRow, mvRows[0], [
        'action',
        'type',
        'amount',
        'currency',
        'player_id',
      ]);
      cell.occurrences = mvRows.length;
      // The mv is the only source carrying the client-side outcome.
      const failed = mvRows.filter(
        (r) => str(r.client_error) && str(r.client_error) !== 'false',
      );
      if (failed.length) {
        cell.state = 'mismatch';
        cell.diffs.push(
          `${failed.length} failed attempt(s) logged: ${failed
            .map((f) => str(f.code) ?? 'error')
            .join(', ')}`,
        );
      }
      cells.mv = cell;
    }

    // --- sync
    const sy = sync.get(id);
    if (sy) {
      cells.sync = compare(mysqlRow, sy, [
        'action',
        'type',
        'amount',
        'currency',
        'player_id',
      ]);
    }

    const hasProblem = (Object.keys(cells) as SourceColumnKey[]).some(
      (k) => cells[k].state === 'mismatch' || cells[k].state === 'missing',
    );

    return {
      transactionId: id,
      trUniqId: m.tr_uniq_id ?? null,
      action: m.action,
      type: m.type,
      amount: m.amount ?? null,
      currency: m.currency ?? null,
      at: parseMysqlTime(m.created_at),
      cells,
      hasProblem,
    };
  });
}

/** realtime has no bet_id but does carry tr_uniq_id — use it as a fallback join. */
function findByTrUniq(
  bundle: BetBundle,
  trUniqId: string | null | undefined,
): Record<string, unknown> | null {
  if (!trUniqId) return null;
  const hit = (bundle.clickhouseRealtime.data ?? []).find((r) => r.tr_uniq_id === trUniqId);
  return (hit as unknown as Record<string, unknown>) ?? null;
}

function compare(
  mysql: Readonly<Record<string, unknown>>,
  other: Readonly<Record<string, unknown>>,
  fields: string[],
): ReconCell {
  const diffs: string[] = [];

  for (const field of fields) {
    const a = mysql[field];
    const b = other[field];
    if (a === null || a === undefined || b === null || b === undefined || b === '') continue;

    const equal = field === 'amount' ? amountsEqual(a, b) : String(a) === String(b);
    if (!equal) diffs.push(`${field}: ${String(a)} vs ${String(b)}`);
  }

  return {
    state: diffs.length ? 'mismatch' : 'present',
    diffs,
    occurrences: 1,
    raw: other,
  };
}

/** Timestamp of a ClickHouse row, for timeline/skew display. */
export function rowTime(raw: Record<string, unknown> | null): Date | null {
  if (!raw) return null;
  return parseClickhouseTime(raw.dt as string | undefined);
}

export interface ReconSummary {
  total: number;
  problems: number;
  /** Sources we could not reach — their agreement is UNKNOWN, not confirmed. */
  unknownSources: number;
  /** Sources actually compared, MySQL included. */
  comparedSources: number;
}

export function summarise(rows: ReconRow[]): ReconSummary {
  const keys = SOURCE_COLUMNS.map((c) => c.key);
  const unknownSources = rows.length
    ? keys.filter((k) => rows.every((r) => r.cells[k].state === 'unknown')).length
    : 0;

  return {
    total: rows.length,
    problems: rows.filter((r) => r.hasProblem).length,
    unknownSources,
    comparedSources: keys.length - unknownSources,
  };
}
